import { useState, useMemo, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Icon } from '@iconify/react';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, DialogActions, TextField, Tabs, Tab, Stack, Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Iconify from '../../../../reusable-components/iconify';
import IconPickerGrid from '../../../../reusable-components/icon-picker-grid/IconPickerGrid';
import { CATEGORY_ICON_SET_PREFIX, CATEGORY_EXTRA_ICONS } from '../../../../utils/cssStyles';
import { isImageIcon } from '../../../../utils/iconHelpers';
import useIconSet from '../../../../hooks/useIconSet';
import axiosInstance from '../../../../utils/axios';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

EmojiPicker.propTypes = {
  icon: PropTypes.string,
  setIcon: PropTypes.func,
  categories: PropTypes.array,
  height: PropTypes.number,
  width: PropTypes.number,
  handleSave: PropTypes.func
};

export default function EmojiPicker({ icon, setIcon, categories, height, width, handleSave }) {
  const [open, setOpen] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [originalIcon] = useState(icon);
  const [emojisUsed, setEmojisUsed] = useState([]);
  const [tab, setTab] = useState('iconSet');
  const [allEmojis, loadingEmojis] = useIconSet(CATEGORY_ICON_SET_PREFIX, open);

  useEffect(() => {
    if (categories) setEmojisUsed(categories.map(cat => cat.icon));
    if (!categories && open) axiosInstance.get("Category/CategoryIcons")
      .then((response) => setEmojisUsed(response.data ?? []))
      .catch(() => enqueueSnackbar("Error fetching icons", { variant: 'error' }));
  }, [open]);

  const handleCancel = () => {
    setIcon(originalIcon);
    setOpen(false);
  };

  const confirmSelection = () => {
    if (handleSave === undefined) setOpen(false);
    else setSaveButtonDisabled(false);
  };

  const sortedEmojis = useMemo(() => {
    const combined = allEmojis.length ? [...allEmojis, ...CATEGORY_EXTRA_ICONS] : allEmojis;
    if (!emojisUsed?.length) return combined;
    return [...combined].sort((a) => emojisUsed.includes(a) ? -1 : 1);
  }, [allEmojis, emojisUsed])

  const filteredEmojis = useMemo(() => sortedEmojis.filter(e => e.includes(searchValue)), [sortedEmojis, searchValue]);

  const { getRootProps, getInputProps, open: openDropzone } = useDropzone({
    accept: { 'image/*': [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => setIcon(e.target.result);
      reader.readAsDataURL(file);
    },
  });

  return (
    <>
      <IconButton onClick={() => { setTab('iconSet'); setOpen(true); }}>
        <Iconify icon={icon} width={width || 60} height={height || 60} />
      </IconButton>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleCancel();
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', display: "flex", alignItems: "center", justifyContent: "center" }}>
          Select an icon
          {tab === 'iconSet' && <TextField
            size="small"
            sx={{ flex: 1, ml: 1, maxWidth: 250 }}
            placeholder="Search Icons"
            inputProps={{ 'aria-label': 'search icons' }}
            InputProps={{
              endAdornment: <IconButton disableRipple type="button" sx={{ p: '0px', cursor: "unset" }} aria-label="search">
                <SearchIcon />
              </IconButton>
            }}
            onChange={(event) => setSearchValue(event.target.value)}
            value={searchValue}
            autoComplete="off"
          />}
          <CloseIconButton onClick={handleCancel} />
        </DialogTitle>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} centered sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Icon Set" value="iconSet" />
          <Tab label="Upload Custom Icon" value="upload" />
        </Tabs>
        <DialogContent sx={{ width: '100%' }}>
          {tab === 'iconSet' && (
            <IconPickerGrid
              icons={filteredEmojis}
              selectedIcon={icon}
              loading={loadingEmojis}
              height={500}
              onSelect={(emoji) => {
                setIcon(emoji);
                confirmSelection();
              }}
            />
          )}
          {tab === 'upload' && (
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              <Box
                sx={{
                  border: 'dashed 1px #4ca5e7',
                  borderRadius: '4px',
                  height: '250px',
                  width: '100%',
                  cursor: 'pointer',
                }}
                {...getRootProps()}
                onClick={() => openDropzone()}
              >
                <input {...getInputProps()} />
                <Stack spacing={1} direction="column" justifyContent="center" alignItems="center" height="100%">
                  <Icon icon="arcticons:folder-upload" width="5rem" height="5rem" color="#8dc63f" />
                  <Stack direction="row">
                    <Typography color="text.secondary">Drag and drop or &nbsp;</Typography>
                    <Typography color="text.secondary" sx={{ textDecoration: 'underline' }}>
                      click here
                    </Typography>
                    <Typography color="text.secondary">&nbsp; to upload an image</Typography>
                  </Stack>
                </Stack>
              </Box>
              {isImageIcon(icon) && (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <Iconify icon={icon} width={64} height={64} />
                  <IconButton onClick={() => setIcon('')}>
                    <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
                  </IconButton>
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                disabled={!isImageIcon(icon)}
                onClick={confirmSelection}
              >
                Use This Icon
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleCancel}>
            Cancel
          </Button>
          {handleSave && <Button
            variant="outlined"
            color="success"
            size="medium"
            sx={{ width: 150, mr: 2 }}
            onClick={() => {
              setSaveButtonDisabled(true);
              setOpen(false);
              handleSave();
            }}
            disabled={saveButtonDisabled}
          >
            Save
          </Button>}
        </DialogActions>
      </Dialog>
    </>
  );
}
